import {
  createTheme,
  ThemeProvider,
  alpha,
  lighten,
} from "@mui/material/styles";
import { esES } from "@mui/x-data-grid";

import { CssBaseline } from "@mui/material";
import React from "react";
import { typographies } from "./typographies";

let theme = createTheme(
  {
    palette: {
      primary: {
        main: "#A7C574",
        contrastText: "#fff",
        light: "#e2f2d7",
        100: "#c6e4af",
        200: "#a9d786",
        300: "#8dc95e",
        400: "#71b53c",
        500: "#A7C574",
        600: "#497627",
        700: "#3b5e1f",
        dark: "#2c4718",
        900: "#1d2f10",
      },
      info: {
        main: "#1d2f10",
        contrastText: "#fff",
        50: "#f3f7e4",
        100: "#e8efc9",
        200: "#dde7ae",
        300: "#d1df93",
        400: "#c6d778",
        500: "#A7C574",
        600: "#a8c139",
        dark: "#869a2e",
        800: "#657422",
        900: "#434d17",
      },
      secondary: {
        main: "#1d2f10",
        contrastText: "#fff",
        50: "#d8e4fb",
        100: "#b1c9f8",
        200: "#8aaef4",
        300: "#6393f0",
        400: "#3c78ed",
        500: "#165DE8",
        600: "#124ec1",
        700: "#0f3e9b",
        800: "#0b2f74",
        900: "#071f4d",
      },
      error: {
        main: "#1d2f10",
        contrastText: "#fff",
        50: "#fef1f1",
        100: "#fcdcdc",
        200: "#f8baba",
        300: "#f59797",
        400: "#f27474",
        500: "#ee5252",
        600: "#d61515",
        700: "#ac1010",
        800: "#810c0c",
        900: "#560808",
      },
      warning: {
        main: "#1d2f10",
        contrastText: "#fff",
        50: "#fef8d7",
        100: "#fdf1ae",
        200: "#fdea86",
        300: "#fce25e",
        400: "#fbdb35",
        500: "#FAD40D",
        600: "#d7b504",
        700: "#ac9104",
        800: "#816d03",
        900: "#564802",
      },
      success: {
        main: "#1d2f10",
        contrastText: "#fff",
        50: "#c6fdd5",
        100: "#8dfaac",
        200: "#54f882",
        300: "#1bf659",
        400: "#08ce40",
        500: "#06952E",
        600: "#057c26",
        700: "#04631f",
        800: "#034b17",
        900: "#02320f",
      },
      common: {
        white: "#ffffff",
        black: "#000000",
      },
      background: {
        default: "#fff",
      },
    },
    typography: {
      fontSize: 13,
      h1: {
        fontSize: "2.5rem",
      },
      h2: {
        fontSize: "2.0rem",
      },
      h3: {
        fontSize: "1.75rem",
      },
      h4: {
        fontSize: "1.5rem",
      },
      h5: {
        fontSize: "1.25rem",
      },
    },
  },
  esES
);

theme = createTheme(theme, {
  components: {
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            "&.Mui-error": {
              "& fieldset": {
                borderColor: "red",
              },
            },
          },
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        main: {
          background: theme.palette.background.default,
          flexGrow: 1,
          marginLeft: "0",
          ".body": {
            width: "100vw",
          },
          "&.open": {
            marginLeft: "13vw",
            ".body": {
              width: "86vw",
            },
          },
        },
        "#root": {
          overflowX: "hidden",
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          height: "100vh",
          "&.login": {
            maxWidth: "100vw",
            maxHeight: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            background:
              "linear-gradient(29.32deg, #616D57 4.01%, #E9FFDE 82.41%)",
            ".box": {
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "auto",
              background:
                "linear-gradient(180deg, #FFFFFF 0%, rgba(255, 255, 255, 0.51) 100%)",
              borderRadius: "20px",
              padding: "0 6rem 12rem 6rem",
              position: "relative",
              ".logo": {
                position: "absolute",
                top: "-1.25rem",
                left: "-3.75rem",
              },
              ".name": {
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "195px",
              },
            },
            ".password-reset": {
              width: "100%",
              display: "block",
              fontSize: "13px",
              color: "#4E4D4DBF",
              textDecoration: "unset",
              "&:hover": {
                color: theme.palette.common.black,
              },
            },
            ".title": {
              fontWeight: "400",
              textAlign: "left",
              width: "100%",
              paddingBottom: "1rem",
            },
            ".subtitle": {
              textAlign: "justify",
            },
            svg: {
              marginRight: "0.75rem",
            },
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        root: {
          "&.dialog": {
            ".MuiPaper-root": {
              overflow: "unset",
            },
            ".logo": {
              position: "absolute",
              top: "-1.25rem",
              left: "-3.75rem",
            },
            ".name": {
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "195px",
            },
            ".component": {
              padding: "4rem 0 2rem 0",
              "&.Login, &.PasswordReset, &.ResetPassword": {
                padding: "3rem 6rem 9rem 6rem",
              },
              "&.diagram": {
                padding: "0",
              },
              ".content": {
                flex: "unset",
                textAlign: "center",
              },
              ".title": {
                fontSize: "25px",
                paddingBottom: "1rem",
              },
              ".description": {
                color: theme.palette.common.black,
                fontSize: "16px",
              },
            },
            ".type": {
              marginBottom: "2rem",
              ".title": {
                fontSize: "20px",
              },
              ".line": {
                content: '""',
                width: "241px",
                height: "7.19px",
                "&.Save": {
                  backgroundColor: "#49762733",
                },
                "&.Confirmed": {
                  backgroundColor: "#9F3831",
                },
              },
            },
            ".actions": {
              padding: "1rem 0 3rem 0",
            },
            ".react-flow": {
              width: "95%!important",
              minHeight: "60vh",
              background: "#f8f9fb",
              ".react-flow__panel": {
                display: "none",
              },
              ".sample": {
                background: "#011b71",
                color: "white",
                fontSize: "16px",
                fontWeight: "700",
                borderColor: "#011b71",
                borderRadius: "50px",
              },
              ".analysis": {
                background: "#3f474c",
                color: "white",
                fontSize: "15px",
                borderColor: "#3f474c",
                borderRadius: "12px",
              },
              ".preparation": {
                fontSize: "15px",
                border: "solid 2px",
                borderColor: "#011b71",
                width: "max-content",
              },
              ".item": {
                fontSize: "15px",
                border: "solid 2px",
                borderColor: "#969696",
              },
            },
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          "&.Mui-selected": {
            fontWeight: "bold",
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          "&.line": {
            border: "unset",
            margin: "auto",
            background: "url(/images/line.svg)",
            backgroundSize: "179px 28px",
            backgroundRepeat: "no-repeat",
            width: "179px",
            height: "28px",
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: "none",
          "&.MuiDrawer-paper": {
            backgroundColor: "#F4F4F4",
            marginTop: "0",
            width: "12vw",
            zIndex: 1201,
          },
          "&.MuiAppBar-root": {
            backgroundColor: "#F4F4F4",
            borderBottom: `1.5px solid ${theme.palette.primary.light}`,
          },
          ".MuiToolbar": {
            minHeight: "86px",
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          boxShadow: "unset",
          marginTop: "2rem",
          ".MuiButtonBase-root": {
            color: theme.palette.common.black,
            textTransform: "none",
            "&.Mui-selected": {
              color: theme.palette.common.black,
              background: "#E1E1E15E",
              borderRadius: "6px 6px 0 0",
            },
          },
          ".MuiTabs-indicator": {
            height: "5px",
          },
          "&.secondary": {
            ".MuiButtonBase-root": {
              color: theme.palette.common.black,
              textTransform: "none",
              "&.Mui-selected": {
                color: theme.palette.common.black,
                background: theme.palette.common.white,
                borderRadius: "6px 6px 0 0",
                fontWeight: "400",
                borderWidth: "4px 4px 0px 4px",
                borderStyle: "solid",
                borderColor: "#606060",
                padding: "14px 40px",
                minHeight: "auto",
              },
            },
            ".MuiTabs-indicator": {
              height: "0",
            },
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: "unset",
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        columnHeaders: {
          borderWidth: "0.5px!important",
          borderColor: "#BCBCBC!important",
          backgroundColor: "#f0f0f0",
        },
        columnHeader: {
          borderWidth: "0.5px!important",
          borderStyle: "solid",
          borderColor: "#BCBCBC!important",
        },
        columnHeaderTitle: {
          fontWeight: "bold",
          whiteSpace: "normal",
          lineHeight: "1",
        },
        columnHeaderTitleContainer: {
          justifyContent: "left!important",
        },
        cell: {
          borderWidth: "0.5px!important",
          borderStyle: "solid",
          borderColor: "#BCBCBC!important",
        },
        cellContent: {
          whiteSpace: "normal",
          lineHeight: "1",
        },
        row: {
          "&.Mui-selected": {
            backgroundColor: alpha(theme.palette.primary.main, 0.2),
            "&:hover": {
              backgroundColor: alpha(theme.palette.primary.main, 0.25),
            },
          },
        },
        root: {
          border: "none",
        },
        footerContainer: {
          justifyContent: "left",
          ".MuiTablePagination-toolbar": {
            paddingLeft: "0",
          },
          ".MuiTablePagination-select": {
            border: "1px solid",
            borderColor: "#588D2F",
            borderRadius: "5px",
          },
        },
        overlayWrapper: {
          width: "100%",
          height: "52px",
          ".no-results": {
            height: "52px",
            borderWidth: "0.5px!important",
            borderStyle: "solid",
            borderColor: "#BCBCBC!important",
          },
        },
      },
    },
    MuiDateCalendar: {
      styleOverrides: {
        root: {
          background: "#EEEDED",
          borderRadius: "9.93px",
          ".MuiPickersDay-root": {
            "&.MuiPickersDay-today": {
              borderRadius: "3.97px",
            },
            "&.Mui-selected": {
              color: theme.palette.common.white,
              backgroundColor: theme.palette.common.black,
              borderRadius: "3.97px",
              "&:hover": {
                color: theme.palette.common.white,
                backgroundColor: theme.palette.common.black,
                borderRadius: "3.97px",
              },
            },
            "&:hover": {
              border: "1px solid",
              borderColor: theme.palette.common.black,
              borderRadius: "3.97px",
            },
          },
        },
      },
    },
    MuiMultiSectionDigitalClock: {
      styleOverrides: {
        root: {
          background: "#EEEDED",
          borderRadius: "9.93px",
          ".MuiMultiSectionDigitalClockSection-item": {
            "&.Mui-selected": {
              color: `${theme.palette.common.white}!important`,
              backgroundColor: `${theme.palette.common.black}!important`,
              borderRadius: "3.97px",
              "&:hover": {
                color: `${theme.palette.common.white}!important`,
                backgroundColor: `${theme.palette.common.black}!important`,
                borderRadius: "3.97px",
              },
            },
            "&:hover": {
              border: "1px solid",
              borderColor: theme.palette.common.black,
              borderRadius: "3.97px",
            },
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          boxShadow: "0px 5px 7.5px 0px #00000040",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: ({ ownerState, theme }: any) => ({
          borderRadius: "6px",
          boxShadow:
            ownerState.variant === "contained"
              ? "-1px -1px 2px rgba(31, 40, 55, 0.1)"
              : "inherit",
          maxHeight: "40px",
          "&.Mui-disabled": {
            color: ownerState.variant === "text" ? "grey" : "white",
            backgroundColor:
              ownerState.variant === "text"
                ? "inherit"
                : lighten(theme.palette.primary.light, 0.65),
          },
        }),
        text: ({ ownerState }: any) => ({
          color: ownerState.variant === "text" ? "#1853C0" : "white",
        }),
        endIcon: ({ ownerState }: any) =>
          ownerState.fullWidth
            ? { position: "absolute", top: 16, right: 10 }
            : null,
        startIcon: ({ ownerState }: any) =>
          ownerState.fullWidth
            ? { position: "absolute", top: 16, left: 15 }
            : null,
      },
      defaultProps: {
        fullWidth: false,
        variant: "contained",
      },
    },
    MuiTab4s: {
      styleOverrides: {
        root: ({ theme }: any) => ({
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          alignContent: "space-between",
          width: "100%",
          backgroundColor: theme.palette.background.default,
          borderRadius: 5,
        }),
        flexContainer: {
          // display: 'inline-flex',
          // position: 'relative',
          // zIndex: 1,
        },
        scroller: ({ theme }: any) => ({
          [theme.breakpoints.up("md")]: {
            // padding: '0 8px',
          },
        }),
        indicator: {
          top: 0,
          bottom: 0,
          height: "auto",
          background: "none",
          "&:after": {
            zIndex: 0,
            content: '""',
            display: "block",
            position: "absolute",
            top: 4,
            left: 4,
            right: 4,
            bottom: 4,
            borderRadius: 5,
            boxShadow: "0 4px 12px 0 rgba(0,0,0,0.16)",
          },
        },
      },
      defaultProps: {
        variant: "fullWidth",
      },
    },
    MuiTab2: {
      styleOverrides: {
        root: ({ theme }: any) => ({
          zIndex: 2,
          "&:hover": {
            opacity: 1,
          },
          padding: "12px 5px",
          minHeight: 44,
          minWidth: 96,
          [theme.breakpoints.up("md")]: {
            minWidth: 120,
          },
        }),
      },
    },
  },
  typography: typographies,
});

type ThemeProps = {
  children: React.ReactNode;
};

export const Theme: React.FC<ThemeProps> = ({ children }) => (
  <ThemeProvider theme={theme}>
    <CssBaseline />
    {children}
  </ThemeProvider>
);
